import { Button, ButtonSize, ButtonVariant } from '@farmasiet/frontendcore-reactcomponents';
import classnames from 'classnames';
import cloneDeep from 'clone';
import * as React from 'react';
import { Key } from 'ts-key-enum';

import { IValidationResponse } from '../../../AdminPortal/Features/Common/IValidationResponse.csharp';
import { getTranslation, keys } from '../../../Resources/Translations';
import { getCamelCaseKey } from '../GetCamelCaseKey';
import { FormInputs } from './FormInputs';
import { FormDataTypes, FormFields, FormSubmitData, FormSubmitStatus, ValidationIssue, ValidationType } from './FormInterfaces';
import { getValidationIssue } from './GetValidationIssue';

interface FormProps {
    validateOnInit?: boolean;
    inputs: FormFields;
    submitButtonText?: string;
    onSubmit: (data: FormSubmitData) => Promise<FormSubmitStatus | boolean>;
    defaultError?: string;
    autocompleteUrl?: string;
    isActive?: boolean;
    showCancelButton?: boolean;
    onCancel?: () => void;
    withButtonLoader?: boolean;
    persistStateOnSubmit?: boolean;
    getProductSkusUrl?: string;
    validateItems?: (skus: string[]) => Promise<IValidationResponse>;
    afterValidateItems?: (validationResponse: IValidationResponse) => void;
    // eslint-disable-next-line
    dependencies?: any[];
    onChange?: () => void;
}

interface FormState {
    inputs: FormFields;
    success?: boolean;
    isFormValid: boolean;
    responseError?: string;
    isChanged: boolean;
    isRequestActive: boolean;
    isFormDisabled: boolean;
}

export default class Form extends React.Component<FormProps, FormState> {
    state: FormState = {
        inputs: {},
        isFormValid: false,
        isChanged: false,
        responseError: '',
        isRequestActive: false,
        success: false,
        isFormDisabled: false
    };

    componentDidMount() {
        this.resetForm();
    }

    componentDidUpdate(prevProps: FormProps) {
        if (prevProps.isActive != this.props.isActive && this.props.isActive) {
            this.resetForm();
        }

        this.updateInputsIfFieldsChanged();
    }

    render() {
        const {
            state: { inputs, isRequestActive, success, responseError, isFormDisabled },
            props: {
                submitButtonText,
                autocompleteUrl,
                onCancel,
                showCancelButton,
                persistStateOnSubmit,
                withButtonLoader,
                getProductSkusUrl,
                validateItems,
                dependencies,
                afterValidateItems
            }
        } = this;

        return (
            <div className={classnames('NewFormWrapper', (isFormDisabled || isRequestActive) && 'NewFormWrapper--Disabled')}>
                <span className="NewForm__RequiredFields">{getTranslation(keys['form.requiredFields'])}</span>
                <form onSubmit={(e) => this.onSubmit(e)} onKeyDown={(e) => e.key === Key.Enter && this.setWasActiveForAll()} className="NewForm">
                    <FormInputs
                        inputs={inputs}
                        setWasActive={this.setWasActive}
                        autocompleteUrl={autocompleteUrl}
                        onChange={this.onChange}
                        validateOnInit={this.props.validateOnInit}
                        collapseInput={this.collapseInput}
                        getProductSkusUrl={getProductSkusUrl}
                        // validateItemsUrl={validateItemsUrl}
                        validate={validateItems}
                        afterValidateItems={afterValidateItems}
                        dependencies={dependencies}
                    />

                    <div className="NewForm__Footer">
                        {onCancel && showCancelButton && !success && (
                            <Button
                                variant={ButtonVariant.Link}
                                size={ButtonSize.Medium}
                                type="button"
                                className="NewForm__CancelButton"
                                onClick={(e) => this.onCancel(e)}
                            >
                                {getTranslation(keys['form.buttons.cancel'])}
                            </Button>
                        )}
                        <Button
                            size={ButtonSize.Medium}
                            className="NewForm__SubmitButton"
                            type="submit"
                            disabled={!withButtonLoader && isRequestActive}
                            loading={withButtonLoader && (isRequestActive || (isFormDisabled && persistStateOnSubmit))}
                            onClick={(e) => this.onSubmit(e)}
                        >
                            {submitButtonText || getTranslation(keys['form.buttons.confirm'])}
                        </Button>
                    </div>
                </form>

                {responseError && <span className="NewForm__ResponseError">{responseError}</span>}
            </div>
        );
    }

    private updateInputsIfFieldsChanged = () => {
        const prevKeys = Object.keys(this.state.inputs).sort();
        const newKeys = Object.keys(this.props.inputs).sort();

        if (JSON.stringify(prevKeys) !== JSON.stringify(newKeys)) {
            const inputs = cloneDeep(this.props.inputs);

            for (const oldInputKey in this.state.inputs) {
                if (inputs.hasOwnProperty(oldInputKey)) {
                    inputs[oldInputKey] = this.state.inputs[oldInputKey];
                }
            }

            this.setState({ inputs: inputs });
        }
    };

    private onCancel = (e: React.MouseEvent) => {
        e.preventDefault();

        this.resetForm();

        this.props.onCancel && this.props.onCancel();
    };

    private onSubmit = (e: React.FormEvent<HTMLFormElement> | React.MouseEvent) => {
        e.preventDefault();
        this.setWasActiveForAll(async () => {
            if (this.validateForm()) {
                this.setState({ isRequestActive: true });
                const response = await this.props.onSubmit(this.getData());

                this.setState({ isRequestActive: false });

                if (!response) return this.handleRequestError();

                if (response !== true && !response.isSuccess) {
                    if (response.validationErrors) return this.assignResponseErrors(response);

                    this.handleRequestError();
                }

                if (this.props.persistStateOnSubmit) {
                    this.setState({ isFormDisabled: true });
                } else {
                    this.resetForm();
                }
            }
        });
    };

    private resetForm = () => {
        this.setState({ inputs: cloneDeep(this.props.inputs), responseError: undefined }, this.validateForm);
    };

    private onChange = (value: FormDataTypes, name: string, validationIssues: ValidationIssue[], trim?: boolean) => {
        if (!this.state.isFormDisabled) {
            this.setState((state: FormState) => {
                const input = state.inputs[name];

                if (value !== input.value) state.responseError = '';
                input.value = trim ? (value as string).trim() : value;
                state.success = false;
                input.validationIssues = input.wasActive ? validationIssues : [];
                state.isChanged = true;
                return state;
            }, this.validateForm);
            this.props.onChange && this.props.onChange();
        }
    };

    private collapseInput = (value: boolean, name: string) => {
        this.setState((state: FormState) => {
            const input = state.inputs[name];

            input.isCollapsed = value;
            return state;
        }, this.validateForm);
    };

    private setWasActive = (name: string) => {
        if (!this.state.success && this.state.inputs.hasOwnProperty(name)) {
            this.setState((state) => {
                const inputs = { ...state.inputs };
                inputs[name].wasActive = true;

                return state;
            });
        }
    };

    private getData = (): FormSubmitData => {
        const formData: FormSubmitData = {};

        for (const key in this.state.inputs) {
            if (this.state.inputs[key].type === 'checkbox') formData[key] = this.state.inputs[key].value;
            else formData[key] = this.state.inputs[key].value || '';
        }

        return formData;
    };

    private assignResponseErrors = (response: FormSubmitStatus) => {
        const inputs = cloneDeep(this.state.inputs);
        let responseError = '';

        for (const validationIssue of response.validationErrors || []) {
            const key = getCamelCaseKey(validationIssue.key);
            if (this.state.inputs.hasOwnProperty(key)) {
                inputs[key].validationIssues = [{ type: ValidationType.Error, message: validationIssue.message }];
            } else {
                responseError = validationIssue.message;
            }
        }

        this.setState({
            inputs,
            isFormValid: false,
            isRequestActive: false,
            success: false,
            responseError
        });
    };

    private handleRequestError = () => {
        this.setState({
            responseError: this.props.defaultError || getTranslation(keys['form.errors.badRequest']),
            isRequestActive: false
        });
    };

    private setWasActiveForAll = (callback?: () => void) => {
        const inputs = cloneDeep(this.state.inputs);

        for (const key in inputs) {
            const input = inputs[key];

            input.wasActive = true;
        }

        this.setState({ inputs }, () => {
            this.validateForm();
            callback && callback();
        });
    };

    private validateForm = (): boolean => {
        let isValid = !this.state.responseError;
        const inputs = this.state.inputs;

        for (const key in inputs) {
            const input = inputs[key];
            let validationIssues = input.validationIssues ?? [];

            validationIssues =
                input.wasActive && ((validationIssues.length === 0 && input.type === 'postalCodeAndCity') || input.type !== 'postalCodeAndCity')
                    ? getValidationIssue(input, inputs, input.value || '')
                    : validationIssues;
            input.validationIssues = validationIssues;

            const requiredInputNotFilled = input.validation?.required?.value && !input.value;

            const errors = validationIssues.filter((x) => x.type === ValidationType.Error);
            if (errors.length > 0 || requiredInputNotFilled) {
                isValid = false;
            }
        }

        this.setState({ isFormValid: isValid, inputs });
        return isValid;
    };
}
